import React, { useMemo } from 'react'

import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import type { SelectOption } from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery'

import { ActiveMemberAtRule } from '../../components/ActiveMemberAtRule'
import { CommentsWrittenRule } from '../../components/CommentsWrittenRule'
import { FirstSubscribedAtRule } from '../../components/FirstSubscribedAtRule'
import { GiftsGivenRule } from '../../components/GiftsGivenRule'
import { LikesGivenRule } from '../../components/LikesGivenRule'
import { LikesReceivedRule } from '../../components/LikesReceivedRule'
import { SelectRule } from '../../components/SelectRule'
import { TierRule } from '../../components/TierRule'
import { validationSchema } from './consts'
import type {
  ActiveMemberAtRule as ActiveMemberAtRuleType,
  CommentsWrittenRule as CommentsWrittenRuleType,
  FirstSubscribedAtRule as FirstSubscribedAtRuleType,
  FormValues,
  GiftsGivenRule as GiftsGivenRuleType,
  LikesGivenRule as LikesGivenRuleType,
  LikesReceivedRule as LikesReceivedRuleType,
  Rules,
  TierRule as TierRuleType,
} from './types'
import { FormFields } from './types'

type Props = {
  initialValues?: FormValues
  onSubmit: (values: FormValues) => void
}
export const useBadgeForm = ({ initialValues, onSubmit }: Props) => {
  const { tiersQuery } = useTiersQuery()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const form = useFormik<FormValues>({
    initialValues: initialValues || {
      [FormFields.badgeType]: 'IconAndText',
      [FormFields.text]: '',
      [FormFields.emoji]: {
        type: 'Emoji',
        emojiIcon: '😀',
      },
      [FormFields.borderColor]: undefined,
      [FormFields.backgroundColor]: 'rgba(0,0,0,0)',
      [FormFields.textColor]: undefined,
      [FormFields.useCustomUsernameColor]: false,
      [FormFields.usernameTextColor]: undefined,
      [FormFields.canBeEarnedByMembers]: true,
      [FormFields.removeWhenRulesNoLongerMet]: true,
      [FormFields.giveToSelectedMembers]: false,
      [FormFields.giveToSelectedTeamStaff]: false,
      [FormFields.rules]: [],
      [FormFields.manuallyAssignedSupporters]: [],
      [FormFields.manuallyAssignedTeamStaff]: [],
      [FormFields.memberStreakBadges]: [],
    },
    onSubmit,
    validationSchema,
    enableReinitialize: true,
  })

  const availableTiers: SelectOption[] = useMemo(() => {
    if (!tiersQuery.data) return []

    return tiersQuery.data.tiers.map((tier) => ({
      label: tier.name,
      value: tier.id.toString(),
      key: tier.id.toString(),
      disabled: !!form.values.rules.find((rule) => rule.type === 'Tier' && rule.tierId === tier.id),
    }))
  }, [tiersQuery.data, form.values.rules])

  const availableRules: SelectOption[] = useMemo(() => {
    const options = [
      {
        label: 'Being a member of a selected tier',
        value: 'Tier' satisfies TierRuleType['type'],
        key: 'Tier' satisfies TierRuleType['type'],
        disabled: availableTiers.length === 0,
      },
      {
        label: 'Number of comments written',
        value: 'CommentsWritten' satisfies CommentsWrittenRuleType['type'],
        key: 'CommentsWritten' satisfies CommentsWrittenRuleType['type'],
        disabled: form.values.rules.some((rule) => rule.type === 'CommentsWritten'),
      },
      {
        label: 'Number of likes given',
        value: 'LikesGiven' satisfies LikesGivenRuleType['type'],
        key: 'LikesGiven' satisfies LikesGivenRuleType['type'],
        disabled: form.values.rules.some((rule) => rule.type === 'LikesGiven'),
      },
      {
        label: 'Number of likes received',
        value: 'LikesReceived' satisfies LikesReceivedRuleType['type'],
        key: 'LikesReceived' satisfies LikesReceivedRuleType['type'],
        disabled: form.values.rules.some((rule) => rule.type === 'LikesReceived'),
      },
      {
        label: 'First subscribed on',
        value: 'FirstSubscribedAt' satisfies FirstSubscribedAtRuleType['type'],
        key: 'FirstSubscribedAt' satisfies FirstSubscribedAtRuleType['type'],
        disabled: form.values.rules.some((rule) => rule.type === 'FirstSubscribedAt'),
      },
      {
        label: 'Member active any time during selected period',
        value: 'ActiveMemberAt' satisfies ActiveMemberAtRuleType['type'],
        key: 'ActiveMemberAt' satisfies ActiveMemberAtRuleType['type'],
        disabled: form.values.rules.some((rule) => rule.type === 'ActiveMemberAt'),
      },
    ]

    if (dashboardFeatureFlags.membershipsGifting.enabled) {
      options.push({
        label: 'Number of gifted subscriptions',
        value: 'GiftsGiven' satisfies GiftsGivenRuleType['type'],
        key: 'GiftsGiven' satisfies GiftsGivenRuleType['type'],
        disabled: form.values.rules.some((rule) => rule.type === 'GiftsGiven'),
      })
    }

    return options
  }, [form.values, availableTiers, dashboardFeatureFlags.membershipsGifting.enabled])

  const getRuleComponent = (rule: Rules[number]) => {
    switch (rule.type) {
      case 'CommentsWritten':
        return <CommentsWrittenRule form={form} options={availableRules} />
      case 'LikesGiven':
        return <LikesGivenRule form={form} options={availableRules} />
      case 'LikesReceived':
        return <LikesReceivedRule form={form} options={availableRules} />
      case 'GiftsGiven':
        return <GiftsGivenRule form={form} options={availableRules} />
      case 'FirstSubscribedAt':
        return <FirstSubscribedAtRule form={form} options={availableRules} />
      case 'ActiveMemberAt':
        return <ActiveMemberAtRule form={form} options={availableRules} />
      case 'Tier':
        return <TierRule form={form} options={availableRules} tiers={availableTiers} rule={rule} />
      case 'EmptyRule':
        return <SelectRule form={form} options={availableRules} />
      default:
        return <div />
    }
  }

  return {
    form,
    availableRules,
    hasEmptyRule: form.values.rules.some((rule) => rule.type === 'EmptyRule'),
    hasAnyRules: form.values.rules.length > 0,
    hasAvailableRules: availableRules.length > 0,
    getRuleComponent,
  }
}
